import React, { useEffect, useState } from "react";
import {
  Activity,
  BarChart3,
  Calendar,
  ChevronRight,
  Search,
  RefreshCcw,
  Users,
  Star,
} from "lucide-react";
import { format } from "date-fns";
import { useApi } from "../../hooks/useApi";
import { StatCard } from './admin-dashboard/StatCard';
import { SessionTrendsChart } from './admin-dashboard/charts/SessionTrendsChart';
import { CounselorPerformanceChart } from './admin-dashboard/charts/CounselorPerformanceChart';
import { AttendanceDistributionChart } from './admin-dashboard/charts/AttendanceDistributionChart';
import { DashboardFilters } from './admin-dashboard/filters/DashboardFilters';
import ReportsSection from "./ReportsSection";
import { DashboardEventBanner } from '../announcements';

const COLORS = {
  primary: "#282C69",
  secondary: "#FCC807",
  accent: "#DC3545",
  success: "#28A745",
  warning: "#FFC107",
  info: "#17A2B8",
};

export default function AdminDashboard() {
  const { get, post } = useApi();
  const [searchQuery, setSearchQuery] = useState('');
  const [state, setState] = useState({
    stats: null,
    trends: [],
    counselors: [],
    clients: [],
    attendance: [],
    loading: true,
    error: null,
    lastUpdated: null,
  });
  const [filters, setFilters] = useState(null);

  const fetchData = async (filterParams = {}) => {
    try {
      setState((prev) => ({ ...prev, loading: true }));

      const [stats, trends, counselors, clients, attendance] = await Promise.all([
        get("/dashboard/stats/admin"),
        get("/dashboard/sessions/trends", { params: filterParams }),
        get("/dashboard/counselors/performance", { params: filterParams }),
        get("/dashboard/clients/progress", { params: filterParams }),
        get("/dashboard/attendance/rates", { params: filterParams }),
      ]);

      setState({
        stats: stats.data.data,
        trends: trends.data.data,
        counselors: counselors.data.data,
        clients: clients.data.data,
        attendance: attendance.data.data,
        loading: false,
        error: null,
        lastUpdated: new Date(),
      });
    } catch (error) {
      console.error("Dashboard Data Fetch Error:", error);
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error.message,
      }));
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => fetchData(filters), 300000); // 5-minute auto-refresh
    return () => clearInterval(interval);
  }, [filters]);

  // Error State
  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-950">
        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl p-8 max-w-md w-full border border-gray-100 dark:border-gray-800">
          <div className="text-center">
            <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
              <Activity className="h-10 w-10 text-red-500 dark:text-red-400" />
            </div>
            <h2 className="text-2xl font-bold mb-3 text-gray-900 dark:text-white">Error Loading Dashboard</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8">{state.error}</p>
            <button
              onClick={() => fetchData(filters)}
              className="w-full bg-usiu-blue-600 text-white font-bold px-6 py-3 rounded-xl hover:bg-usiu-blue-700 shadow-lg shadow-usiu-blue-500/25 transition-all active:scale-95"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8">
          <div>
            <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight">System Overview</h1>
            <p className="text-gray-500 dark:text-gray-400 mt-1 font-medium italic">Comprehensive Counseling Analytics</p>
          </div>
          <div className="flex items-center gap-4 w-full md:w-auto">
            <div className="relative flex-1 md:flex-none">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 h-4 w-4" />
              <input
                type="text"
                placeholder="Search analytics..."
                className="w-full md:w-64 pl-10 pr-4 py-2.5 bg-white dark:bg-slate-900 border border-gray-200 dark:border-gray-800 rounded-xl focus:outline-none focus:ring-2 focus:ring-usiu-blue-500/20 text-sm transition-all dark:text-white"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-3 bg-white dark:bg-slate-900 px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm">
              <span className="text-xs font-bold text-gray-500 dark:text-gray-400 tabular-nums">
                {state.lastUpdated ? format(state.lastUpdated, "MMM d, h:mm a") : "Never"}
              </span>
              <button
                onClick={() => fetchData(filters)}
                disabled={state.loading}
                className="p-1.5 rounded-2xl hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-50 text-gray-500 dark:text-gray-400"
              >
                <RefreshCcw className={`h-4 w-4 ${state.loading ? "animate-spin text-usiu-blue-500" : ""}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Event Announcements Banner */}
        <DashboardEventBanner />

        {/* Filters */}
        <DashboardFilters
          onFilter={setFilters}
          onExport={(format) => {/* Implement export handling */}}
          isLoading={state.loading}
        />

        {/* Main Grid */}
        <div className="grid grid-cols-12 gap-6">
          {/* Left Column - Stats and Charts */}
          <div className="col-span-8">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-6 mb-6">
              <StatCard
                title="Total Users"
                value={state.stats?.totalUsers}
                icon={Users}
                isLoading={state.loading}
                trend={5}
                color={COLORS.primary}
              />
              <StatCard
                title="Active Counselors"
                value={state.stats?.activeCounselors}
                icon={Calendar}
                isLoading={state.loading}
                color={COLORS.secondary}
              />
            </div>

            {/* Session Trends */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800 mb-6 overflow-hidden">
              <div className="flex justify-between items-center p-6 border-b border-gray-100 dark:border-gray-800">
                <div className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-usiu-blue-600 dark:text-usiu-blue-400" />
                  <h2 className="font-bold text-gray-900 dark:text-white">Session Trends</h2>
                </div>
                <div className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-800 px-3 py-1.5 rounded-2xl transition-colors">
                  <span className="text-sm font-bold text-gray-500 dark:text-gray-400">Detailed Report</span>
                  <ChevronRight className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                </div>
              </div>
              <div className="p-6">
                <SessionTrendsChart data={state.trends} isLoading={state.loading} />
              </div>
            </div>

            {/* Counselor Performance */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800 mb-6 p-6 overflow-hidden">
              <CounselorPerformanceChart data={state.counselors} isLoading={state.loading} />
            </div>
          </div>

          {/* Right Column - Status, Attendance, Reports */}
          <div className="col-span-4 space-y-6">
            {/* System Performance */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800 p-6">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                <Activity className="h-5 w-5 mr-3" style={{ color: COLORS.primary }} />
                System Health
              </h2>
              <div className="space-y-6">
                {/* Average Rating */}
                <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl border border-gray-100 dark:border-gray-700/50">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-sm font-bold text-gray-600 dark:text-gray-400">
                      Average Rating
                    </span>
                    <span className="text-lg font-black text-gray-900 dark:text-white">
                      {state.stats?.systemMetrics?.averageRating}/5
                    </span>
                  </div>
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`h-5 w-5 ${
                          star <= state.stats?.systemMetrics?.averageRating
                            ? "text-yellow-400 fill-current"
                            : "text-gray-300"
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Attendance Distribution */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800 p-6">
              <AttendanceDistributionChart data={state.attendance} isLoading={state.loading} />
            </div>

            {/* Active Counselors Sidebar */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800 p-6">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-6">Top Counselors</h2>
              <div className="space-y-4">
                {state.counselors.slice(0, 3).map((counselor, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-usiu-blue-50 dark:bg-usiu-blue-900/20 flex items-center justify-center text-usiu-blue-600 dark:text-usiu-blue-400 font-bold border border-usiu-blue-100 dark:border-usiu-blue-800/50">
                        {counselor.name?.charAt(0)}
                      </div>
                      <div>
                        <p className="font-bold text-gray-900 dark:text-white text-sm">{counselor.name}</p>
                        <p className="text-[10px] uppercase font-heavy tracking-wider text-gray-500 dark:text-gray-400 mt-0.5">
                          Professional Counselor
                        </p>
                      </div>
                    </div>
                    <div
                      className={`w-2 h-2 rounded-full ${
                        counselor.completionRate >= 80 ? "bg-green-500" : "bg-yellow-500"
                      }`}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Reports Section */}
            <ReportsSection />
          </div>
        </div>
      </div>
    </div>
  );
}
